// 知识库服务
const API_BASE = '/api';

/** 带 HTTP 状态码的请求错误（便于界面区分 403 无权、404 等） */
export type KnowledgeBaseRequestError = Error & { status: number };

function createRequestError(message: string, status: number): KnowledgeBaseRequestError {
  const err = new Error(message) as KnowledgeBaseRequestError;
  err.status = status;
  return err;
}

/** 从 catch 到的异常中读取知识库 API 的 HTTP 状态码 */
export function getKnowledgeBaseErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const s = (error as { status: unknown }).status;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
}

// 辅助函数：获取token
const getToken = () => {
  return localStorage.getItem('token');
};

// 辅助函数：API请求
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw createRequestError('未授权，请重新登录', 401);
    }
    const error = await response.json().catch(() => ({ message: '请求失败' }));
    throw createRequestError(error.message || '请求失败', response.status);
  }

  return response.json();
};

export interface VideoInfo {
  key: string;
  url: string;
  fileName: string;
  fileSize: number;
  duration: number;
  resolution: {
    width: number;
    height: number;
  };
  thumbnail: string;
  status: 'uploading' | 'processing' | 'ready' | 'failed';
  variants?: Array<{
    quality: string;
    url: string;
    fileSize: number;
  }>;
}

export interface Attachment {
  name: string;
  url: string;
  size: number;
  type: 'document' | 'image' | 'other';
  mimeType: string;
}

export interface KnowledgeBase {
  _id: string;
  title: string;
  content: string;
  summary: string;
  categoryId?: string;
  category?: string;
  tags: string[];
  attachments: Attachment[];
  video?: VideoInfo;
  version: number;
  versionHistory?: Array<{
    version: number;
    content: string;
    updatedBy: string;
    updatedAt: string;
    changeNote: string;
  }>;
  createdBy: {
    _id: string;
    username: string;
    email: string;
  };
  updatedBy: {
    _id: string;
    username: string;
    email: string;
  };
  accessLevel: 'public' | 'private' | 'restricted';
  allowedUsers: string[];
  viewCount: number;
  likeCount: number;
  favoriteCount: number;
  relatedDocuments: string[];
  relatedInstruments: string[];
  relatedConsumables: string[];
  relatedReagents: string[];
  relatedLiteratures: string[];
  status: 'draft' | 'published' | 'archived';
  isPinned: boolean;
  isRecommended: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeBaseFormData {
  title: string;
  content: string;
  summary?: string;
  categoryId?: string;
  category?: string;
  tags?: string[];
  attachments?: Attachment[];
  video?: VideoInfo;
  accessLevel?: 'public' | 'private' | 'restricted';
  allowedUsers?: string[];
  relatedDocuments?: string[];
  relatedInstruments?: string[];
  relatedConsumables?: string[];
  relatedReagents?: string[];
  relatedLiteratures?: string[];
  status?: 'draft' | 'published' | 'archived';
}

export interface KnowledgeBaseListParams {
  page?: number;
  limit?: number;
  keyword?: string;
  /** 关联分类 ID */
  categoryId?: string;
  /** 分类名称（与表单预设 category 一致） */
  category?: string;
  tag?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** 列表分栏：public | private | restricted */
  accessTab?: 'public' | 'private' | 'restricted';
}

export interface KnowledgeBaseComment {
  _id: string;
  knowledgeBaseId: string;
  userId: {
    _id: string;
    username: string;
    email: string;
  };
  content: string;
  parentId?: string;
  likeCount: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export const knowledgeBaseService = {
  // 获取知识库列表
  getList: async (params: KnowledgeBaseListParams = {}): Promise<{
    knowledgeBases: KnowledgeBase[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.keyword) queryParams.append('keyword', params.keyword);
    if (params.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params.category) queryParams.append('category', params.category);
    if (params.tag) queryParams.append('tag', params.tag);
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.accessTab) queryParams.append('accessTab', params.accessTab);

    const response = await apiRequest(`/knowledge-base?${queryParams.toString()}`);
    return response.data;
  },

  // 获取知识库详情
  getById: async (id: string): Promise<KnowledgeBase> => {
    const response = await apiRequest(`/knowledge-base/${id}`);
    return response.data.knowledgeBase;
  },

  /** 详情页第一步：元数据，不含正文与版本历史，不计阅读 */
  getOverview: async (id: string): Promise<KnowledgeBase> => {
    const response = await apiRequest(`/knowledge-base/${id}/overview`);
    return response.data.knowledgeBase;
  },

  /** 详情页第二步：正文并更新阅读量 */
  getBody: async (id: string): Promise<{ content: string; viewCount: number }> => {
    const response = await apiRequest(`/knowledge-base/${id}/body`);
    return response.data;
  },

  // 创建知识库文档
  create: async (data: KnowledgeBaseFormData): Promise<KnowledgeBase> => {
    const response = await apiRequest('/knowledge-base', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data.knowledgeBase;
  },

  // 更新知识库文档
  update: async (id: string, data: Partial<KnowledgeBaseFormData>): Promise<KnowledgeBase> => {
    const response = await apiRequest(`/knowledge-base/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data.knowledgeBase;
  },

  // 删除知识库文档
  delete: async (id: string): Promise<void> => {
    await apiRequest(`/knowledge-base/${id}`, { method: 'DELETE' });
  },

  // 点赞
  like: async (id: string): Promise<{ likeCount: number }> => {
    const response = await apiRequest(`/knowledge-base/${id}/like`, {
      method: 'POST',
    });
    return response.data;
  },

  // 收藏
  favorite: async (id: string): Promise<{ favoriteCount: number }> => {
    const response = await apiRequest(`/knowledge-base/${id}/favorite`, {
      method: 'POST',
    });
    return response.data;
  },

  // 获取评论列表
  getComments: async (id: string, page: number = 1, limit: number = 20): Promise<{
    comments: KnowledgeBaseComment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await apiRequest(`/knowledge-base/${id}/comments?page=${page}&limit=${limit}`);
    return response.data;
  },

  // 添加评论
  addComment: async (id: string, content: string, parentId?: string): Promise<KnowledgeBaseComment> => {
    const response = await apiRequest(`/knowledge-base/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content, parentId }),
    });
    return response.data.comment;
  },
};

